import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Gift, Eye, Award, Star } from "lucide-react";
import { api } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fullImageUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${BACKEND_URL}${u}`;
};

const HERO_IMG = "https://customer-assets.emergentagent.com/job_stock-showcase-2/artifacts/hg9wynru_IMG_0396%20%281%29.jpeg";

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const [rewards, setRewards] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setFeatured(r.data.filter((p) => p.featured).slice(0, 6)));
    api.get("/rewards").then((r) => setRewards(r.data.filter((x) => x.active).slice(0, 6)));
  }, []);

  return (
    <div className="min-h-[calc(100vh-80px)]" data-testid="landing-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-8 fade-up">
            <span className="label-eyebrow">Noctalis · Programme privé</span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              Bienvenue dans <span className="italic gold-text">Noctalis</span> — où chaque visite vous rapproche d'une récompense.
            </h1>
            <p className="text-[#a3a8a4] max-w-xl text-base leading-relaxed">
              Découvrez notre collection de parfums, lunettes et accessoires soigneusement choisis. Cumulez des points à chaque achat et débloquez des récompenses exclusives.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link to="/produits" className="inline-flex items-center gap-2 bg-[#d4af37] text-black px-7 py-3.5 font-medium tracking-widest uppercase text-xs hover:bg-[#e6c27a] transition-colors btn-shimmer" data-testid="cta-products">
                Voir le catalogue <ArrowRight size={16}/>
              </Link>
              <Link to="/register" className="inline-flex items-center gap-2 border border-[#d4af37] text-[#d4af37] px-7 py-3.5 font-medium tracking-widest uppercase text-xs hover:bg-[#d4af37]/10 transition-colors" data-testid="cta-register">
                Rejoindre le programme
              </Link>
            </div>

            <div className="grid grid-cols-3 gap-6 pt-12 border-t border-[rgba(253,251,247,0.08)]">
              <Stat icon={<Sparkles size={18}/>} label="Programme" value="Fidélité"/>
              <Stat icon={<Gift size={18}/>} label="Récompenses" value="Exclusives"/>
              <Stat icon={<Eye size={18}/>} label="Catalogue" value="Privé"/>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up-delay-2">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={HERO_IMG} alt="Collection Noctalis" className="w-full h-full object-cover product-img" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050706] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="label-eyebrow mb-2">Collection Noctalis</div>
                <div className="font-display text-2xl">Parfums, lunettes & accessoires sélectionnés à la main.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><Star size={12}/> Coups de cœur</div>
              <h2 className="font-display text-4xl lg:text-5xl">Les pièces du moment</h2>
            </div>
            <Link to="/produits" className="text-sm gold-text hover:underline flex items-center gap-2" data-testid="landing-see-all-products">Voir tout le catalogue <ArrowRight size={14}/></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => (
              <Link to="/produits" key={p.id} className="product-card group block" data-testid={`landing-product-${p.id}`}>
                <div className="aspect-[4/5] overflow-hidden bg-[#0a0e0b] mb-4 relative">
                  {p.image_url ? (
                    <img src={fullImageUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover product-img"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#a3a8a4]">Aucune image</div>
                  )}
                  {p.stock > 0 && (
                    <div className="absolute top-3 right-3 px-3 py-1 text-[10px] tracking-widest uppercase bg-[#0a0e0b]/80 backdrop-blur-sm border border-[rgba(253,251,247,0.1)] gold-text">
                      En stock
                    </div>
                  )}
                </div>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-xl">{p.name}</div>
                    <div className="text-xs text-[#a3a8a4] mt-1">{p.category}</div>
                    {p.price > 0 && <div className="text-base mt-1">{p.price.toFixed(2)} €</div>}
                    {p.points_reward > 0 && <div className="text-xs gold-text mt-1">+{p.points_reward} pts à l'achat</div>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {rewards.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><Gift size={12}/> Programme de fidélité</div>
              <h2 className="font-display text-4xl lg:text-5xl">Ce que vous pouvez gagner</h2>
              <p className="text-[#a3a8a4] mt-3 max-w-xl">Cumulez des points à chaque achat en boutique et échangez-les contre nos récompenses.</p>
            </div>
            <Link to="/register" className="text-sm gold-text hover:underline flex items-center gap-2" data-testid="landing-join-program">Rejoindre le programme <ArrowRight size={14}/></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rewards.map((r) => (
              <div key={r.id} className="bg-[#0a0e0b] border border-[rgba(253,251,247,0.08)] hover:border-[#d4af37]/40 p-8 transition-colors" data-testid={`landing-reward-${r.id}`}>
                <div className="flex justify-between items-start mb-5">
                  <Award className="gold-text" size={28}/>
                  <span className="label-eyebrow">{r.points_cost} pts</span>
                </div>
                <div className="font-display text-2xl mb-2">{r.name}</div>
                <p className="text-sm text-[#a3a8a4] line-clamp-3">{r.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[rgba(253,251,247,0.08)] border border-[rgba(253,251,247,0.08)]">
          <Feature title="Découvrez" body="Explorez notre vitrine privée de parfums, lunettes et accessoires sélectionnés avec soin."/>
          <Feature title="Cumulez" body="Recevez des points de fidélité à chaque achat en boutique."/>
          <Feature title="Gagnez" body="Échangez vos points contre des récompenses, cadeaux et expériences exclusives."/>
        </div>
      </section>
    </div>
  );
}

function Stat({icon, label, value}) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[#d4af37] mb-2">{icon}<span className="label-eyebrow">{label}</span></div>
      <div className="font-display text-2xl">{value}</div>
    </div>
  );
}

function Feature({title, body}) {
  return (
    <div className="bg-[#0a0e0b] p-10">
      <div className="label-eyebrow mb-4">{title}</div>
      <p className="text-[#a3a8a4] leading-relaxed">{body}</p>
    </div>
  );
}
